import {
  ChevronDown,
  ChevronLeft,
  Check,
  ClipboardList,
  Download,
  Gauge,
  ImageDown,
  List,
  LoaderCircle,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Share2,
  Trash2,
  UtensilsCrossed,
  X,
  createIcons,
} from "lucide";
import { sampleWithoutReplacement, secureRandomInt } from "../shared/random";
import { DISH_CATEGORIES, type Dish, type DishCategory, type WheelResponse } from "../shared/types";
import { ApiError, createDish, getWheel } from "./api";
import {
  addOrderRequest,
  emptyGuestOrderDraft,
  normalizeGuestName,
  parseGuestOrderDraft,
  serializeGuestOrderDraft,
  toggleOrderDish,
  type GuestOrderDraft,
  type GuestOrderItem,
} from "./guest-order";
import { generateOrderImage, type GeneratedOrderImage } from "./order-image";
import { generatePickyImage, type GeneratedPickyImage } from "./picky-image";
import {
  PICKY_CATEGORIES,
  PICKY_PATTERN_PROFILES,
  PICKY_PROFILES,
  PICKY_SCORE_PROFILES,
  emptyPickyTestDraft,
  getPickyResult,
  ingredientIdsForCategory,
  parsePickyTestDraft,
  serializePickyTestDraft,
  type PickyCategory,
  type PickyResult,
  type PickyTestDraft,
} from "./picky-test";
import "./styles.css";

type AppState =
  | { mode: "loading" }
  | { mode: "ready"; wheel: WheelResponse }
  | { mode: "error"; message: string };

type CategorySelection = DishCategory | "all";
type AppView = "wheel" | "pool" | "order" | "picky";
type Feedback = { kind: "error" | "success"; message: string } | null;

const DEFAULT_WHEEL_SLUG = "foesa-kitchen";
const CANDIDATE_COUNT = 12;
const SEGMENT_COLORS = ["#F2A69B", "#BFD58C", "#F0CE63", "#E7AAC3", "#A9C6EB", "#9ED4CD"];
const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)");
const collator = new Intl.Collator("zh-CN");
const categoryValues = new Set<string>(DISH_CATEGORIES.map(({ value }) => value));

const appRoot = document.querySelector<HTMLDivElement>("#app");
if (!appRoot) throw new Error("App root is missing.");
const app: HTMLDivElement = appRoot;

let state: AppState = { mode: "loading" };
let candidates: Dish[] = [];
let winner: Dish | null = null;
let rotation = 0;
let spinning = false;
let spinTimer: number | null = null;
let spinAnimation: Animation | null = null;
let selectedCategory: CategorySelection = "all";
let appView: AppView = viewFromLocation();
let poolQuery = "";
let orderQuery = "";
let orderCategory: CategorySelection = "all";
let guestOrder: GuestOrderDraft = emptyGuestOrderDraft();
let customOrderFeedback: Feedback = null;
let orderExportFeedback: Feedback = null;
let generatedOrderImage: (GeneratedOrderImage & { url: string }) | null = null;
let pickyDraft: PickyTestDraft = emptyPickyTestDraft();
let confirmedPickyResult: PickyResult | null = null;
let pickyExportFeedback: Feedback = null;
let generatedPickyImage: (GeneratedPickyImage & { url: string }) | null = null;

function viewFromLocation(): AppView {
  const view = new URLSearchParams(location.search).get("view");
  if (view === "dishes") return "pool";
  if (view === "order") return "order";
  if (view === "picky") return "picky";
  return "wheel";
}

function currentSlug(): string {
  return new URLSearchParams(location.search).get("wheel")?.trim() || DEFAULT_WHEEL_SLUG;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function compactLabel(name: string): string {
  const chars = Array.from(name);
  return chars.length > 8 ? `${chars.slice(0, 7).join("")}…` : name;
}

function dishPlaceholderMarkup(): string {
  return `<span class="dish-image-placeholder" aria-hidden="true"><i data-lucide="utensils-crossed"></i></span>`;
}

function dishMediaMarkup(dish: Dish, className: string, eager = false): string {
  const content = dish.imageUrl
    ? `<img src="${escapeHtml(dish.imageUrl)}" alt="${escapeHtml(dish.name)}的图片" width="720" height="540" loading="${eager ? "eager" : "lazy"}" decoding="async" data-dish-image />`
    : dishPlaceholderMarkup();
  return `<span class="dish-media ${className}">${content}</span>`;
}

function categoryLabel(category: DishCategory): string {
  return DISH_CATEGORIES.find(({ value }) => value === category)?.label ?? category;
}

function orderCategoryLabel(category: GuestOrderItem["category"]): string {
  return category === "custom" ? "特别想吃" : categoryLabel(category);
}

function normalizedSearch(value: string): string {
  return value.normalize("NFKC").trim().toLocaleLowerCase("zh-CN");
}

function orderStorageKey(slug: string): string {
  return `food-wheel:guest-order:${slug}`;
}

function persistGuestOrder(): void {
  if (state.mode !== "ready") return;
  try {
    localStorage.setItem(orderStorageKey(state.wheel.slug), serializeGuestOrderDraft(guestOrder));
  } catch {
    // The order remains usable in memory when storage is unavailable.
  }
}

function persistPickyDraft(): void {
  try {
    localStorage.setItem("food-wheel:picky-test:v1", serializePickyTestDraft(pickyDraft));
  } catch {
    // The test remains usable in memory when storage is unavailable.
  }
}

function dishesForSelection(wheel: WheelResponse): Dish[] {
  return selectedCategory === "all"
    ? wheel.dishes
    : wheel.dishes.filter((dish) => dish.category === selectedCategory);
}

function wheelGradient(size: number): string {
  if (size === 0) return "var(--soft-line)";
  const step = 360 / size;
  const stops = Array.from({ length: size }, (_, index) => {
    const color = SEGMENT_COLORS[index % SEGMENT_COLORS.length] ?? SEGMENT_COLORS[0];
    return `${color} ${index * step}deg ${(index + 1) * step}deg`;
  });
  return `conic-gradient(from ${-step / 2}deg, ${stops.join(", ")})`;
}

function labelMarkup(dishes: readonly Dish[]): string {
  const step = 360 / Math.max(dishes.length, 1);
  return dishes.map((dish, index) => {
    const radians = ((index * step) - 90) * Math.PI / 180;
    const left = 50 + (36 * Math.cos(radians));
    const top = 50 + (36 * Math.sin(radians));
    return `<span class="wheel-label" data-wheel-label style="left:${left.toFixed(3)}%;top:${top.toFixed(3)}%;--counter-rotation:${-rotation}deg" title="${escapeHtml(dish.name)}">${escapeHtml(compactLabel(dish.name))}</span>`;
  }).join("");
}

function wheelDiscMarkup(): string {
  const category = selectedCategory === "all" ? "全部分类" : categoryLabel(selectedCategory);
  return `<div class="wheel-disc" data-wheel-disc role="img" aria-label="${category}随机选菜转盘" style="--rotation:${rotation}deg;--wheel-background:${wheelGradient(candidates.length)}">
    ${labelMarkup(candidates)}
  </div>`;
}

function resultMarkup(): string {
  if (candidates.length === 0) {
    return `<p class="result-label">当前分类</p>
      <p class="result-value">还没有菜名</p>`;
  }
  if (!winner) {
    return `<p class="result-label">今天的答案</p>
      <p class="result-value">还没揭晓</p>`;
  }
  return `<div class="result-card">
    ${dishMediaMarkup(winner, "result-media", true)}
    <div class="result-copy">
      <p class="result-label"><i data-lucide="check" aria-hidden="true"></i><span>就吃这个</span></p>
      <p class="result-value">${escapeHtml(winner.name)}</p>
    </div>
  </div>`;
}

function loadingMarkup(): string {
  return `<div class="app-shell">
    <header class="topbar"><span class="brand"><i data-lucide="utensils-crossed" aria-hidden="true"></i><span>今天吃什么</span></span></header>
    <main id="main-content" class="loading-view" aria-busy="true">
      <section class="loading-wheel" aria-label="正在加载共享菜池"><span></span></section>
      <aside class="loading-controls"><span></span><span></span><span></span></aside>
    </main>
  </div>`;
}

function errorMarkup(message: string): string {
  return `<div class="app-shell app-shell--compact">
    <header class="topbar"><span class="brand"><i data-lucide="utensils-crossed" aria-hidden="true"></i><span>今天吃什么</span></span></header>
    <main id="main-content" class="error-view" tabindex="-1">
      <p class="section-label">共享菜池</p>
      <h1>暂时没能打开</h1>
      <p>${escapeHtml(message)}</p>
      <button class="primary-button fit-button" type="button" data-action="retry"><i data-lucide="refresh-cw" aria-hidden="true"></i><span>重新加载</span></button>
    </main>
  </div>`;
}

function headerMarkup(wheel: WheelResponse, view: AppView): string {
  return `<header class="topbar">
    <a class="brand" href="/" aria-label="今天吃什么首页"><i data-lucide="utensils-crossed" aria-hidden="true"></i><span>${escapeHtml(wheel.title)}</span></a>
    <div class="topbar-actions">
      ${view === "wheel" ? '<button class="icon-button share-button" type="button" data-action="share" aria-label="分享转盘" title="分享转盘"><i data-lucide="share-2" aria-hidden="true"></i></button>' : ""}
      ${view !== "order" ? '<button class="header-list-button header-order-button" type="button" data-action="open-order" aria-label="来点菜" title="来点菜"><i data-lucide="clipboard-list" aria-hidden="true"></i><span>来点菜</span></button>' : ""}
      ${view !== "pool" ? '<button class="header-list-button" type="button" data-action="open-pool" aria-label="菜的清单" title="菜的清单"><i data-lucide="list" aria-hidden="true"></i><span>菜的清单</span></button>' : ""}
      ${view !== "picky" ? '<button class="header-list-button" type="button" data-action="open-picky" aria-label="挑食指数" title="挑食指数"><i data-lucide="gauge" aria-hidden="true"></i><span>挑食指数</span></button>' : ""}
    </div>
  </header>`;
}

function dishFormMarkup(): string {
  return `<form class="dish-form" data-dish-form novalidate>
    <div class="form-field">
      <label for="dish-name">新的菜名</label>
      <input id="dish-name" name="dishName" maxlength="20" autocomplete="off" placeholder="例如：番茄炒蛋" aria-describedby="dish-help dish-feedback" required />
      <p id="dish-help" class="field-help">最多 20 个字符</p>
    </div>
    <div class="form-field">
      <label for="dish-category">分类</label>
      <select id="dish-category" name="dishCategory" aria-describedby="dish-feedback" required>
        <option value="">请选择分类</option>
        ${DISH_CATEGORIES.map(({ value, label }) => `<option value="${value}">${label}</option>`).join("")}
      </select>
    </div>
    <button class="primary-button add-button" type="submit"><i data-lucide="plus" aria-hidden="true"></i><span>加入菜池</span></button>
    <p id="dish-feedback" class="field-feedback" data-dish-feedback role="status"></p>
  </form>`;
}

function categoryFilterMarkup(
  current: CategorySelection = selectedCategory,
  action = "select-category",
  legend = "选择分类",
): string {
  const options = [{ value: "all", label: "全部" } as const, ...DISH_CATEGORIES];
  return `<fieldset class="category-filter">
    <legend>${escapeHtml(legend)}</legend>
    <div class="category-options">
      ${options.map(({ value, label }) => `<button type="button" data-action="${action}" data-category="${value}" aria-pressed="${current === value}" class="category-option${current === value ? " is-selected" : ""}">${label}</button>`).join("")}
    </div>
  </fieldset>`;
}

function readyMarkup(wheel: WheelResponse): string {
  return `<div class="app-shell">
    <a class="skip-link" href="#main-content">跳到主要内容</a>
    ${headerMarkup(wheel, "wheel")}
    <main id="main-content" class="workspace" tabindex="-1">
      <section class="wheel-area" aria-labelledby="page-title">
        <div class="page-heading">
          <p class="section-label">design by 小羊</p>
          <h1 id="page-title">今天吃什么？</h1>
        </div>
        ${categoryFilterMarkup()}
        <div class="wheel-stage" data-wheel-stage>
          <span class="wheel-pointer" aria-hidden="true"></span>
          ${wheelDiscMarkup()}
          <button class="spin-button" type="button" data-action="spin" aria-label="转动转盘" ${candidates.length === 0 ? "disabled" : ""}><span>开转</span></button>
        </div>
        <div class="result-band" data-result aria-live="polite">${resultMarkup()}</div>
      </section>
      <aside class="control-rail">
        <section aria-labelledby="add-title">
          <p class="section-label">共享编辑</p>
          <h2 id="add-title">加入一道想吃的</h2>
          ${dishFormMarkup()}
        </section>
      </aside>
    </main>
    <div class="live-region" data-live aria-live="polite"></div>
  </div>`;
}

function dishBankMarkup(wheel: WheelResponse): string {
  const visibleCount = wheel.dishes.filter((dish) => dish.name.toLocaleLowerCase("zh-CN").includes(poolQuery.toLocaleLowerCase("zh-CN"))).length;
  return `<div class="app-shell dish-bank-shell">
    <a class="skip-link" href="#main-content">跳到主要内容</a>
    ${headerMarkup(wheel, "pool")}
    <main id="main-content" class="dish-bank-section">
      <div class="dish-bank-heading">
        <button class="back-button" type="button" data-action="return-wheel"><i data-lucide="chevron-left" aria-hidden="true"></i><span>返回转盘</span></button>
        <p class="section-label">共 <span data-pool-count>${wheel.dishes.length}</span> 道菜</p>
        <h1 tabindex="-1">菜的清单</h1>
      </div>
      <section class="bank-add-section" aria-labelledby="bank-add-title">
        <h2 id="bank-add-title">加入一道想吃的</h2>
        ${dishFormMarkup()}
      </section>
      <div class="bank-search-block">
        <label for="pool-search">搜索菜名</label>
        <div class="bank-search-control">
          <i data-lucide="search" aria-hidden="true"></i>
          <input id="pool-search" type="search" autocomplete="off" placeholder="输入菜名" value="${escapeHtml(poolQuery)}" data-pool-search aria-describedby="pool-search-status" />
          <button type="button" data-action="clear-pool-search" aria-label="清空搜索" title="清空搜索" ${poolQuery ? "" : "hidden"}><i data-lucide="x" aria-hidden="true"></i></button>
        </div>
        <p id="pool-search-status" class="bank-search-status" data-dialog-count aria-live="polite">${poolQuery ? `找到 ${visibleCount} 道菜` : `显示全部 ${wheel.dishes.length} 道菜`}</p>
      </div>
      <div class="dish-bank-grid" data-dish-grid>${poolItemsMarkup(wheel.dishes, poolQuery)}</div>
    </main>
    <div class="live-region" data-live aria-live="polite"></div>
  </div>`;
}

function poolItemsMarkup(dishes: readonly Dish[], query = ""): string {
  const normalized = query.normalize("NFKC").trim().toLocaleLowerCase("zh-CN");
  const filtered = dishes
    .filter((dish) => dish.name.toLocaleLowerCase("zh-CN").includes(normalized))
    .sort((a, b) => collator.compare(a.name, b.name));
  if (filtered.length === 0) return `<p class="pool-empty">没有找到这个菜名</p>`;
  return filtered.map((dish) => `<article class="dish-item dish-card">
    ${dishMediaMarkup(dish, "dish-card-media")}
    <div class="dish-item-copy">
      <h3>${escapeHtml(dish.name)}</h3>
      <p class="dish-category">${categoryLabel(dish.category)}</p>
    </div>
  </article>`).join("");
}

function visibleOrderDishes(wheel: WheelResponse): Dish[] {
  const normalized = normalizedSearch(orderQuery);
  return wheel.dishes
    .filter((dish) => orderCategory === "all" || dish.category === orderCategory)
    .filter((dish) => normalizedSearch(dish.name).includes(normalized))
    .sort((a, b) => collator.compare(a.name, b.name));
}

function isOrderDishSelected(dish: Dish): boolean {
  const nameKey = normalizedSearch(dish.name);
  return guestOrder.items.some((item) => item.dishId === dish.id || item.key === nameKey);
}

function orderDishOptionsMarkup(wheel: WheelResponse): string {
  const dishes = visibleOrderDishes(wheel);
  if (dishes.length === 0) {
    return `<div class="order-empty">
      <p>没有找到这道菜</p>
      <button class="secondary-button" type="button" data-action="focus-custom-order"><i data-lucide="plus" aria-hidden="true"></i><span>写下想吃的</span></button>
    </div>`;
  }
  return dishes.map((dish) => {
    const selected = isOrderDishSelected(dish);
    return `<button class="order-dish-option${selected ? " is-selected" : ""}" type="button" data-action="toggle-order-dish" data-order-dish-id="${dish.id}" aria-pressed="${selected}">
      ${dishMediaMarkup(dish, "order-option-media")}
      <span class="order-option-copy"><strong>${escapeHtml(dish.name)}</strong><small>${categoryLabel(dish.category)}</small></span>
      <span class="order-option-check" aria-hidden="true"><i data-lucide="check"></i></span>
    </button>`;
  }).join("");
}

function selectedOrderItemsMarkup(): string {
  if (guestOrder.items.length === 0) {
    return `<div class="order-summary-empty">
      <i data-lucide="clipboard-list" aria-hidden="true"></i>
      <p>还没有选菜</p>
    </div>`;
  }
  return `<ul class="order-selection-list">
    ${guestOrder.items.map((item) => `<li>
      <span class="order-selection-copy"><strong>${escapeHtml(item.name)}</strong><small>${orderCategoryLabel(item.category)}</small></span>
      <button class="order-remove-button" type="button" data-action="remove-order-item" data-order-item-key="${escapeHtml(item.key)}" aria-label="删除${escapeHtml(item.name)}" title="删除${escapeHtml(item.name)}"><i data-lucide="x" aria-hidden="true"></i></button>
    </li>`).join("")}
  </ul>`;
}

function orderExportHint(): Feedback {
  const guest = normalizeGuestName(guestOrder.guestName);
  if (!guest.ok) return { kind: "error", message: guestOrder.guestName ? guest.message : "填写名字后即可保存" };
  if (guestOrder.items.length === 0) return { kind: "error", message: "至少选择一道菜" };
  return orderExportFeedback;
}

function orderSummaryMarkup(): string {
  const guest = normalizeGuestName(guestOrder.guestName);
  const canExport = guest.ok && guestOrder.items.length > 0;
  const feedback = orderExportHint();
  return `<aside class="order-summary" id="order-summary" aria-labelledby="order-summary-title">
    <div class="order-summary-heading">
      <div>
        <p class="section-label">已选 <span data-order-count>${guestOrder.items.length}</span> 道</p>
        <h2 id="order-summary-title">我的点菜单</h2>
      </div>
      ${guestOrder.items.length > 0 ? '<button class="order-clear-button" type="button" data-action="clear-order"><i data-lucide="trash-2" aria-hidden="true"></i><span>清空</span></button>' : ""}
    </div>
    <div data-order-selection-list>${selectedOrderItemsMarkup()}</div>
    <div class="order-summary-actions">
      <button class="primary-button order-save-button" type="button" data-action="save-order-image" ${canExport ? "" : "disabled"}><i data-lucide="image-down" aria-hidden="true"></i><span>保存点菜单</span></button>
      <p class="field-feedback" data-order-export-feedback data-kind="${feedback?.kind ?? ""}" role="status">${escapeHtml(feedback?.message ?? "")}</p>
    </div>
  </aside>`;
}

function guestOrderMarkup(wheel: WheelResponse): string {
  const visibleCount = visibleOrderDishes(wheel).length;
  return `<div class="app-shell order-shell">
    <a class="skip-link" href="#main-content">跳到主要内容</a>
    ${headerMarkup(wheel, "order")}
    <main id="main-content" class="order-workspace" tabindex="-1">
      <section class="order-catalog" aria-labelledby="order-page-title">
        <div class="order-page-heading">
          <button class="back-button" type="button" data-action="return-wheel"><i data-lucide="chevron-left" aria-hidden="true"></i><span>返回转盘</span></button>
          <p class="section-label">朋友来之前</p>
          <h1 id="order-page-title" tabindex="-1">来我家吃什么？</h1>
          <p class="order-intro">选好想吃的，见面就开饭。</p>
        </div>

        <div class="form-field order-name-field">
          <label for="guest-name">你的名字</label>
          <input id="guest-name" name="guestName" maxlength="20" autocomplete="name" placeholder="怎么称呼你" value="${escapeHtml(guestOrder.guestName)}" data-order-name aria-describedby="guest-name-help" />
          <p id="guest-name-help" class="field-help">图片上会显示这个名字</p>
        </div>

        <section class="order-picker" aria-labelledby="order-picker-title">
          <h2 id="order-picker-title">选择想吃的菜</h2>
          <div class="order-search-block">
            <label for="order-search">搜索菜名</label>
            <div class="bank-search-control">
              <i data-lucide="search" aria-hidden="true"></i>
              <input id="order-search" type="search" autocomplete="off" placeholder="输入菜名" value="${escapeHtml(orderQuery)}" data-order-search aria-describedby="order-search-status" />
              <button type="button" data-action="clear-order-search" aria-label="清空搜索" title="清空搜索" ${orderQuery ? "" : "hidden"}><i data-lucide="x" aria-hidden="true"></i></button>
            </div>
            <p id="order-search-status" class="bank-search-status" data-order-search-status aria-live="polite">显示 ${visibleCount} 道菜</p>
          </div>
          ${categoryFilterMarkup(orderCategory, "select-order-category", "按分类筛选")}

          <form class="custom-order-form" data-custom-order-form novalidate>
            <div class="form-field">
              <label for="custom-order-name">菜单里没有？</label>
              <div class="custom-order-control">
                <input id="custom-order-name" name="customOrderName" maxlength="20" autocomplete="off" placeholder="写下你想吃的" aria-describedby="custom-order-help custom-order-feedback" />
                <button class="secondary-button" type="submit"><i data-lucide="plus" aria-hidden="true"></i><span>加入点菜单</span></button>
              </div>
              <p id="custom-order-help" class="field-help">只加入你的这张点菜单</p>
              <p id="custom-order-feedback" class="field-feedback" data-custom-order-feedback data-kind="${customOrderFeedback?.kind ?? ""}" role="status">${escapeHtml(customOrderFeedback?.message ?? "")}</p>
            </div>
          </form>

          <div class="order-dish-grid" data-order-dish-grid>${orderDishOptionsMarkup(wheel)}</div>
        </section>
      </section>
      ${orderSummaryMarkup()}
    </main>
    <div class="order-mobile-bar" aria-label="当前点菜单">
      <span>已选 <strong data-order-mobile-count>${guestOrder.items.length}</strong> 道</span>
      <button class="primary-button" type="button" data-action="view-order-summary"><i data-lucide="clipboard-list" aria-hidden="true"></i><span>查看点菜单</span></button>
    </div>
    <div class="live-region" data-live aria-live="polite"></div>
  </div>`;
}

function isPickyIngredientSelected(ingredientId: string): boolean {
  return pickyDraft.selectedIngredientIds.includes(ingredientId);
}

function pickyCategoryMarkup(category: PickyCategory): string {
  const expanded = pickyDraft.expandedCategoryIds.includes(category.id);
  const selectedCount = category.ingredients.filter(({ id }) => isPickyIngredientSelected(id)).length;
  const allSelected = selectedCount === category.ingredients.length;
  const panelId = `picky-category-${category.id}`;
  return `<section class="picky-category" data-picky-category="${category.id}">
    <div class="picky-category-heading">
      <button class="picky-category-toggle" type="button" data-action="toggle-picky-category" data-picky-category-id="${category.id}" aria-expanded="${expanded}" aria-controls="${panelId}">
        <span class="picky-category-name">${escapeHtml(category.name)}</span>
        <span class="picky-category-count" data-picky-category-count>${selectedCount > 0 ? `已选 ${selectedCount}` : `${category.ingredients.length} 项`}</span>
        <i data-lucide="chevron-down" aria-hidden="true"></i>
      </button>
      <button class="picky-category-bulk" type="button" data-action="toggle-picky-category-all" data-picky-category-id="${category.id}">${allSelected ? "清空本类" : "整类不吃"}</button>
    </div>
    <div class="picky-ingredient-panel" id="${panelId}" ${expanded ? "" : "hidden"}>
      <div class="picky-ingredient-grid">
        ${category.ingredients.map((ingredient) => {
          const selected = isPickyIngredientSelected(ingredient.id);
          return `<label class="picky-ingredient${selected ? " is-selected" : ""}" for="picky-${ingredient.id}">
            <input id="picky-${ingredient.id}" type="checkbox" value="${ingredient.id}" data-picky-ingredient ${selected ? "checked" : ""} />
            <span>${escapeHtml(ingredient.name)}</span>
          </label>`;
        }).join("")}
      </div>
    </div>
  </section>`;
}

function pickyScoreRulerMarkup(index: number): string {
  const activeSegments = Math.ceil(index / 5);
  return `<div class="picky-score-ruler" aria-hidden="true">
    ${Array.from({ length: 20 }, (_, segment) => `<i${segment < activeSegments ? ' class="is-active"' : ""}></i>`).join("")}
  </div>`;
}

function pickyResultContentMarkup(result: PickyResult): string {
  const focus = result.primaryPattern
    ? `最明显：${result.primaryPattern.profile.focusLabel}（${result.primaryPattern.selectedCount}/${result.primaryPattern.totalCount}）`
    : "还没有明显的忌口类别";
  return `<div class="picky-result-heading">
      <p class="section-label">挑食指数</p>
      <div class="picky-score"><strong>${result.index}</strong><span>/ 100</span></div>
      ${pickyScoreRulerMarkup(result.index)}
    </div>
    <div class="picky-persona">
      <p>你的饮食人设</p>
      <h2 id="picky-result-title">${escapeHtml(result.profile.name)}</h2>
      <div class="picky-tags">${result.profile.tags.map((tag) => `<span>${escapeHtml(tag)}</span>`).join("")}</div>
      <p class="picky-description">${escapeHtml(result.profile.description)}</p>
      <div class="picky-result-meta">
        <p>${escapeHtml(focus)}</p>
        <p>不吃 ${result.selectedCount} / ${result.totalCount} 种食材</p>
      </div>
    </div>
    <div class="picky-result-actions">
      <button class="primary-button" type="button" data-action="save-picky-image"><i data-lucide="image-down" aria-hidden="true"></i><span>重新生成海报</span></button>
      ${result.selectedCount > 0 ? '<button class="secondary-button" type="button" data-action="reset-picky-test"><i data-lucide="rotate-ccw" aria-hidden="true"></i><span>重新测试</span></button>' : ""}
      <p class="field-feedback" data-picky-export-feedback data-kind="${pickyExportFeedback?.kind ?? ""}" role="status">${escapeHtml(pickyExportFeedback?.message ?? "")}</p>
    </div>`;
}

function pickyPendingContentMarkup(selectedCount: number): string {
  return `<div class="picky-confirm-state">
      <p class="section-label">准备生成</p>
      <div class="picky-confirm-count"><strong>${selectedCount}</strong><span>项忌口</span></div>
      <h2 id="picky-result-title">确认后揭晓结果</h2>
      <p>确认当前选择后，才会计算挑食指数并生成你的专属海报。</p>
    </div>
    <div class="picky-result-actions">
      <button class="primary-button" type="button" data-action="save-picky-image"><i data-lucide="check" aria-hidden="true"></i><span>确认并生成</span></button>
      ${selectedCount > 0 ? '<button class="secondary-button" type="button" data-action="reset-picky-test"><i data-lucide="rotate-ccw" aria-hidden="true"></i><span>清空选择</span></button>' : ""}
      <p class="field-feedback" data-picky-export-feedback data-kind="${pickyExportFeedback?.kind ?? ""}" role="status">${escapeHtml(pickyExportFeedback?.message ?? "")}</p>
    </div>`;
}

function pickyProfileGroupMarkup(title: string, profiles: typeof PICKY_PROFILES, currentId: string | null): string {
  return `<section class="picky-atlas-group" aria-labelledby="picky-atlas-${escapeHtml(title)}">
    <h3 id="picky-atlas-${escapeHtml(title)}">${escapeHtml(title)}</h3>
    <div class="picky-atlas-grid">
      ${profiles.map((profile) => `<article class="picky-atlas-item${profile.id === currentId ? " is-current" : ""}" data-picky-profile-id="${profile.id}" ${profile.id === currentId ? 'aria-current="true"' : ""}>
        <h4>${escapeHtml(profile.name)}</h4>
        <p>${profile.tags.map(escapeHtml).join(" / ")}</p>
      </article>`).join("")}
    </div>
  </section>`;
}

function pickyAtlasMarkup(currentProfileId: string | null): string {
  return `<details class="picky-atlas">
    <summary><span>20 种人设图鉴</span><i data-lucide="chevron-down" aria-hidden="true"></i></summary>
    <div class="picky-atlas-content">
      ${pickyProfileGroupMarkup("通用人设", PICKY_SCORE_PROFILES, currentProfileId)}
      ${pickyProfileGroupMarkup("口味人设", PICKY_PATTERN_PROFILES, currentProfileId)}
    </div>
  </details>`;
}

function pickyMobileBarContentMarkup(selectedCount: number): string {
  if (confirmedPickyResult) {
    return `<span>挑食指数 <strong>${confirmedPickyResult.index}</strong></span>
      <button class="primary-button" type="button" data-action="view-picky-result"><i data-lucide="gauge" aria-hidden="true"></i><span>查看结果</span></button>`;
  }
  return `<span>已选 <strong>${selectedCount}</strong> 项</span>
    <button class="primary-button" type="button" data-action="save-picky-image"><i data-lucide="check" aria-hidden="true"></i><span>确认并生成</span></button>`;
}

function pickyTestMarkup(wheel: WheelResponse): string {
  const draftResult = getPickyResult(pickyDraft.selectedIngredientIds);
  return `<div class="app-shell picky-shell">
    <a class="skip-link" href="#main-content">跳到主要内容</a>
    ${headerMarkup(wheel, "picky")}
    <main id="main-content" class="picky-workspace" tabindex="-1">
      <section class="picky-picker" aria-labelledby="picky-page-title">
        <div class="picky-page-heading">
          <button class="back-button" type="button" data-action="return-wheel"><i data-lucide="chevron-left" aria-hidden="true"></i><span>返回转盘</span></button>
          <p class="section-label">了解你的口味</p>
          <h1 id="picky-page-title" tabindex="-1">测测你的挑食指数</h1>
          <div class="picky-heading-meta">
            <p>勾出你明确不吃的食材</p>
            <p>已选 <strong data-picky-selected-count>${draftResult.selectedCount}</strong> 项</p>
          </div>
        </div>
        <div class="picky-categories">${PICKY_CATEGORIES.map(pickyCategoryMarkup).join("")}</div>
        ${pickyAtlasMarkup(confirmedPickyResult?.profile.id ?? null)}
      </section>
      <aside class="picky-result" id="picky-result" aria-labelledby="picky-result-title" data-picky-result>
        ${confirmedPickyResult ? pickyResultContentMarkup(confirmedPickyResult) : pickyPendingContentMarkup(draftResult.selectedCount)}
      </aside>
    </main>
    <div class="picky-mobile-bar" aria-label="挑食测试操作" data-picky-mobile-bar>
      ${pickyMobileBarContentMarkup(draftResult.selectedCount)}
    </div>
    <div class="live-region" data-live aria-live="polite"></div>
  </div>`;
}

function initIcons(): void {
  createIcons({
    icons: { ChevronDown, ChevronLeft, Check, ClipboardList, Download, Gauge, ImageDown, List, LoaderCircle, Plus, RefreshCw, RotateCcw, Search, Share2, Trash2, UtensilsCrossed, X },
    attrs: { "stroke-width": 1.8 },
  });
}

function render(): void {
  disposeGeneratedOrderImage();
  disposeGeneratedPickyImage();
  const content = state.mode === "loading"
    ? loadingMarkup()
    : state.mode === "ready"
      ? appView === "pool"
        ? dishBankMarkup(state.wheel)
        : appView === "order"
          ? guestOrderMarkup(state.wheel)
          : appView === "picky" ? pickyTestMarkup(state.wheel) : readyMarkup(state.wheel)
      : errorMarkup(state.message);
  app.innerHTML = content;
  initIcons();
}

function setLive(message: string): void {
  const live = document.querySelector<HTMLElement>("[data-live]");
  if (live) live.textContent = message;
}

function refreshWheelDisc(): void {
  const current = document.querySelector<HTMLElement>("[data-wheel-disc]");
  if (!current) return;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = wheelDiscMarkup();
  const next = wrapper.firstElementChild;
  if (next) current.replaceWith(next);
}

function finishSpin(selected: Dish): void {
  if (!spinning) return;
  spinning = false;
  winner = selected;
  if (spinTimer !== null) window.clearTimeout(spinTimer);
  spinTimer = null;
  spinAnimation = null;
  document.querySelectorAll<HTMLElement>("[data-wheel-disc], [data-wheel-label]").forEach((element) => {
    element.getAnimations().forEach((animation) => animation.cancel());
  });
  document.querySelector("[data-wheel-stage]")?.classList.remove("is-spinning");
  const button = document.querySelector<HTMLButtonElement>("[data-action='spin']");
  if (button) {
    button.disabled = false;
    button.querySelector("span")!.textContent = "再转一次";
  }
  const result = document.querySelector<HTMLElement>("[data-result]");
  if (result) {
    result.innerHTML = resultMarkup();
    result.classList.add("has-result");
    initIcons();
  }
  navigator.vibrate?.(18);
  setLive(`今天就吃${selected.name}`);
}

function spinFrames(from: number, to: number, counter = false): Keyframe[] {
  const delta = to - from;
  const values = [from, from + (delta * 0.08), from + (delta * 0.72), to];
  const offsets = [0, 0.16, 0.6, 1];
  const easings = ["cubic-bezier(0.4, 0, 1, 1)", "linear", "cubic-bezier(0, 0, 0.2, 1)"];
  return values.map((value, index) => ({
    transform: counter
      ? `translate(-50%, -50%) rotate(${-value}deg)`
      : `rotate(${value}deg)`,
    offset: offsets[index],
    ...(easings[index] ? { easing: easings[index] } : {}),
  }));
}

function spin(): void {
  if (state.mode !== "ready" || spinning) return;
  const availableDishes = dishesForSelection(state.wheel);
  if (availableDishes.length === 0) return;
  if (winner) {
    candidates = sampleWithoutReplacement(availableDishes, CANDIDATE_COUNT);
    winner = null;
    refreshWheelDisc();
    const result = document.querySelector<HTMLElement>("[data-result]");
    if (result) {
      result.innerHTML = resultMarkup();
      result.classList.remove("has-result");
    }
  }

  const selectedIndex = secureRandomInt(candidates.length);
  const selected = candidates[selectedIndex];
  if (!selected) return;
  spinning = true;
  const startRotation = rotation;
  const step = 360 / candidates.length;
  const desired = (360 - (selectedIndex * step)) % 360;
  const currentNormalized = ((rotation % 360) + 360) % 360;
  const alignment = (desired - currentNormalized + 360) % 360;
  rotation += (360 * 5) + alignment;

  const stage = document.querySelector<HTMLElement>("[data-wheel-stage]");
  const disc = document.querySelector<HTMLElement>("[data-wheel-disc]");
  const button = document.querySelector<HTMLButtonElement>("[data-action='spin']");
  stage?.classList.add("is-spinning");
  if (button) {
    button.disabled = true;
    button.querySelector("span")!.textContent = "转动中";
  }

  if (!disc || reduceMotion.matches || typeof disc.animate !== "function") {
    if (disc) {
      disc.style.setProperty("--rotation", `${rotation}deg`);
      disc.querySelectorAll<HTMLElement>("[data-wheel-label]").forEach((label) => {
        label.style.setProperty("--counter-rotation", `${-rotation}deg`);
      });
    }
    queueMicrotask(() => finishSpin(selected));
    return;
  }

  const timing: KeyframeAnimationOptions = {
    duration: 3800,
    fill: "both",
  };
  spinAnimation = disc.animate(spinFrames(startRotation, rotation), timing);
  disc.querySelectorAll<HTMLElement>("[data-wheel-label]").forEach((label) => {
    label.animate(spinFrames(startRotation, rotation, true), timing);
    label.style.setProperty("--counter-rotation", `${-rotation}deg`);
  });
  disc.style.setProperty("--rotation", `${rotation}deg`);
  spinAnimation.addEventListener("finish", () => finishSpin(selected), { once: true });
  spinTimer = window.setTimeout(() => {
    finishSpin(selected);
  }, 4200);
}

function selectCategory(value: string): void {
  if (state.mode !== "ready" || spinning) return;
  if (value !== "all" && !categoryValues.has(value)) return;
  selectedCategory = value as CategorySelection;
  candidates = sampleWithoutReplacement(dishesForSelection(state.wheel), CANDIDATE_COUNT);
  winner = null;
  refreshWheelDisc();
  document.querySelectorAll<HTMLButtonElement>("[data-action='select-category']").forEach((button) => {
    const selected = button.dataset.category === selectedCategory;
    button.classList.toggle("is-selected", selected);
    button.setAttribute("aria-pressed", String(selected));
  });
  const result = document.querySelector<HTMLElement>("[data-result]");
  if (result) {
    result.innerHTML = resultMarkup();
    result.classList.remove("has-result");
  }
  const spinButton = document.querySelector<HTMLButtonElement>("[data-action='spin']");
  if (spinButton) {
    spinButton.disabled = candidates.length === 0;
    spinButton.querySelector("span")!.textContent = "开转";
  }
  const label = selectedCategory === "all" ? "全部分类" : categoryLabel(selectedCategory);
  setLive(`已选择${label}`);
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const input = document.createElement("textarea");
  input.className = "clipboard-fallback";
  input.value = text;
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  input.remove();
}

async function shareWheel(): Promise<void> {
  const url = `${location.origin}/`;
  if (navigator.share) {
    await navigator.share({ title: "今天吃什么", text: "转一下，看看今天吃什么。", url });
    return;
  }
  await copyText(url);
  setLive("转盘链接已复制");
}

function setAppView(view: AppView, pushHistory = true): void {
  appView = view;
  poolQuery = "";
  orderQuery = "";
  orderCategory = "all";
  customOrderFeedback = null;
  orderExportFeedback = null;
  pickyExportFeedback = null;
  if (pushHistory) {
    const url = new URL(location.href);
    if (view === "pool") url.searchParams.set("view", "dishes");
    else if (view === "order") url.searchParams.set("view", "order");
    else if (view === "picky") url.searchParams.set("view", "picky");
    else url.searchParams.delete("view");
    history.pushState({}, "", `${url.pathname}${url.search}`);
  }
  render();
  requestAnimationFrame(() => document.querySelector<HTMLElement>("#main-content h1")?.focus({ preventScroll: true }));
}

function filterPool(query: string): void {
  if (state.mode !== "ready") return;
  const grid = document.querySelector<HTMLElement>("[data-dish-grid]");
  const count = document.querySelector<HTMLElement>("[data-dialog-count]");
  const normalized = query.normalize("NFKC").trim().toLocaleLowerCase("zh-CN");
  const total = state.wheel.dishes.filter((dish) => dish.name.toLocaleLowerCase("zh-CN").includes(normalized)).length;
  if (grid) grid.innerHTML = poolItemsMarkup(state.wheel.dishes, query);
  if (count) count.textContent = query ? `找到 ${total} 道菜` : `显示全部 ${state.wheel.dishes.length} 道菜`;
  initIcons();
}

function refreshOrderCatalog(): void {
  if (state.mode !== "ready") return;
  const grid = document.querySelector<HTMLElement>("[data-order-dish-grid]");
  const status = document.querySelector<HTMLElement>("[data-order-search-status]");
  if (grid) grid.innerHTML = orderDishOptionsMarkup(state.wheel);
  if (status) status.textContent = `显示 ${visibleOrderDishes(state.wheel).length} 道菜`;
  initIcons();
}

function refreshOrderView(focusSelector?: string): void {
  render();
  if (!focusSelector) return;
  requestAnimationFrame(() => document.querySelector<HTMLElement>(focusSelector)?.focus({ preventScroll: true }));
}

function selectOrderCategory(value: string): void {
  if (state.mode !== "ready") return;
  if (value !== "all" && !categoryValues.has(value)) return;
  orderCategory = value as CategorySelection;
  document.querySelectorAll<HTMLButtonElement>("[data-action='select-order-category']").forEach((button) => {
    const selected = button.dataset.category === orderCategory;
    button.classList.toggle("is-selected", selected);
    button.setAttribute("aria-pressed", String(selected));
  });
  refreshOrderCatalog();
  const label = orderCategory === "all" ? "全部分类" : categoryLabel(orderCategory);
  setLive(`点菜页已筛选${label}`);
}

function toggleGuestOrderDish(dishId: number): void {
  if (state.mode !== "ready") return;
  const dish = state.wheel.dishes.find(({ id }) => id === dishId);
  if (!dish) return;
  const wasSelected = isOrderDishSelected(dish);
  guestOrder.items = toggleOrderDish(guestOrder.items, dish);
  customOrderFeedback = null;
  orderExportFeedback = null;
  persistGuestOrder();
  refreshOrderView(`[data-order-dish-id="${dish.id}"]`);
  setLive(`${dish.name}${wasSelected ? "已移出" : "已加入"}点菜单`);
}

function removeGuestOrderItem(key: string): void {
  const item = guestOrder.items.find((candidate) => candidate.key === key);
  if (!item) return;
  guestOrder.items = guestOrder.items.filter((candidate) => candidate.key !== key);
  orderExportFeedback = null;
  persistGuestOrder();
  refreshOrderView("#order-summary-title");
  setLive(`${item.name}已移出点菜单`);
}

function clearGuestOrder(): void {
  if (guestOrder.items.length === 0 || !window.confirm("清空当前点菜单？")) return;
  guestOrder.items = [];
  customOrderFeedback = null;
  orderExportFeedback = null;
  persistGuestOrder();
  refreshOrderView("#order-picker-title");
  setLive("点菜单已清空");
}

function submitCustomOrder(form: HTMLFormElement): void {
  if (state.mode !== "ready") return;
  const input = form.elements.namedItem("customOrderName");
  if (!(input instanceof HTMLInputElement)) return;
  const result = addOrderRequest(guestOrder.items, state.wheel.dishes, input.value);
  customOrderFeedback = { kind: result.ok ? "success" : "error", message: result.message };
  if (!result.ok) {
    const feedback = document.querySelector<HTMLElement>("[data-custom-order-feedback]");
    if (feedback) {
      feedback.dataset.kind = "error";
      feedback.textContent = result.message;
    }
    input.select();
    return;
  }
  guestOrder.items = result.items;
  orderExportFeedback = null;
  persistGuestOrder();
  input.value = "";
  refreshOrderView("#custom-order-name");
  setLive(result.message);
}

function updateOrderExportControls(): void {
  const guest = normalizeGuestName(guestOrder.guestName);
  const canExport = guest.ok && guestOrder.items.length > 0;
  const button = document.querySelector<HTMLButtonElement>("[data-action='save-order-image']");
  if (button) button.disabled = !canExport;
  const feedback = document.querySelector<HTMLElement>("[data-order-export-feedback]");
  const hint = orderExportHint();
  if (feedback) {
    feedback.dataset.kind = hint?.kind ?? "";
    feedback.textContent = hint?.message ?? "";
  }
}

function disposeGeneratedOrderImage(): void {
  document.querySelector<HTMLDialogElement>("[data-order-image-dialog]")?.remove();
  if (!generatedOrderImage) return;
  URL.revokeObjectURL(generatedOrderImage.url);
  generatedOrderImage = null;
}

function disposeGeneratedPickyImage(): void {
  document.querySelector<HTMLDialogElement>("[data-picky-image-dialog]")?.remove();
  if (!generatedPickyImage) return;
  URL.revokeObjectURL(generatedPickyImage.url);
  generatedPickyImage = null;
}

function orderImageFile(): File | null {
  if (!generatedOrderImage) return null;
  return new File([generatedOrderImage.blob], generatedOrderImage.fileName, { type: "image/png" });
}

function openOrderImagePreview(image: GeneratedOrderImage, guestName: string): void {
  disposeGeneratedOrderImage();
  const url = URL.createObjectURL(image.blob);
  generatedOrderImage = { ...image, url };
  const file = orderImageFile();
  const canShare = Boolean(file && typeof navigator.share === "function" && navigator.canShare?.({ files: [file] }));
  const dialog = document.createElement("dialog");
  dialog.className = "order-image-dialog";
  dialog.dataset.orderImageDialog = "";
  dialog.setAttribute("aria-labelledby", "order-image-title");
  dialog.innerHTML = `<div class="order-image-dialog-header">
      <div>
        <p class="section-label">图片已生成</p>
        <h2 id="order-image-title" tabindex="-1">预览点菜单</h2>
      </div>
      <button class="icon-button" type="button" data-action="close-order-image" aria-label="关闭预览" title="关闭预览"><i data-lucide="x" aria-hidden="true"></i></button>
    </div>
    <div class="order-image-preview"><img src="${escapeHtml(url)}" alt="${escapeHtml(guestName)}的点菜单预览" width="${image.width}" height="${image.height}" /></div>
    <div class="order-image-actions">
      <button class="primary-button" type="button" data-action="download-order-image"><i data-lucide="download" aria-hidden="true"></i><span>下载图片</span></button>
      ${canShare ? '<button class="secondary-button" type="button" data-action="share-order-image"><i data-lucide="share-2" aria-hidden="true"></i><span>分享图片</span></button>' : ""}
    </div>`;
  app.appendChild(dialog);
  dialog.addEventListener("close", () => disposeGeneratedOrderImage(), { once: true });
  initIcons();
  dialog.showModal();
  requestAnimationFrame(() => dialog.querySelector<HTMLElement>("#order-image-title")?.focus());
}

function downloadOrderImage(): void {
  if (!generatedOrderImage) return;
  const anchor = document.createElement("a");
  anchor.href = generatedOrderImage.url;
  anchor.download = generatedOrderImage.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setLive("点菜单图片已下载");
}

async function shareOrderImage(): Promise<void> {
  const file = orderImageFile();
  if (!file || typeof navigator.share !== "function" || !navigator.canShare?.({ files: [file] })) return;
  await navigator.share({ files: [file], title: "我的点菜单", text: "这是我想吃的菜。" });
}

function refreshPickyResult(): void {
  const draftResult = getPickyResult(pickyDraft.selectedIngredientIds);
  document.querySelectorAll<HTMLElement>("[data-picky-selected-count]").forEach((node) => {
    node.textContent = String(draftResult.selectedCount);
  });

  for (const category of PICKY_CATEGORIES) {
    const section = document.querySelector<HTMLElement>(`[data-picky-category="${category.id}"]`);
    if (!section) continue;
    const selectedCount = category.ingredients.filter(({ id }) => isPickyIngredientSelected(id)).length;
    const count = section.querySelector<HTMLElement>("[data-picky-category-count]");
    if (count) count.textContent = selectedCount > 0 ? `已选 ${selectedCount}` : `${category.ingredients.length} 项`;
    const bulk = section.querySelector<HTMLButtonElement>("[data-action='toggle-picky-category-all']");
    if (bulk) bulk.textContent = selectedCount === category.ingredients.length ? "清空本类" : "整类不吃";
  }

  const resultPanel = document.querySelector<HTMLElement>("[data-picky-result]");
  if (resultPanel) {
    resultPanel.innerHTML = confirmedPickyResult
      ? pickyResultContentMarkup(confirmedPickyResult)
      : pickyPendingContentMarkup(draftResult.selectedCount);
  }
  document.querySelectorAll<HTMLElement>("[data-picky-profile-id]").forEach((item) => {
    const current = item.dataset.pickyProfileId === confirmedPickyResult?.profile.id;
    item.classList.toggle("is-current", current);
    if (current) item.setAttribute("aria-current", "true");
    else item.removeAttribute("aria-current");
  });
  const mobileBar = document.querySelector<HTMLElement>("[data-picky-mobile-bar]");
  if (mobileBar) mobileBar.innerHTML = pickyMobileBarContentMarkup(draftResult.selectedCount);
  initIcons();
}

function togglePickyIngredient(ingredientId: string, selected: boolean): void {
  const ingredient = PICKY_CATEGORIES.flatMap(({ ingredients }) => ingredients).find(({ id }) => id === ingredientId);
  if (!ingredient) return;
  const selectedIds = new Set(pickyDraft.selectedIngredientIds);
  if (selected) selectedIds.add(ingredientId);
  else selectedIds.delete(ingredientId);
  pickyDraft.selectedIngredientIds = [...selectedIds];
  confirmedPickyResult = null;
  pickyExportFeedback = null;
  persistPickyDraft();
  const input = document.querySelector<HTMLInputElement>(`[data-picky-ingredient][value="${ingredientId}"]`);
  input?.closest(".picky-ingredient")?.classList.toggle("is-selected", selected);
  refreshPickyResult();
  setLive(`${ingredient.name}${selected ? "已标记为不吃" : "已取消"}`);
}

function pickyCategoryFromId(categoryId: string): PickyCategory | null {
  return PICKY_CATEGORIES.find(({ id }) => id === categoryId) ?? null;
}

function togglePickyCategory(categoryId: string): void {
  const category = pickyCategoryFromId(categoryId);
  if (!category) return;
  const expanded = new Set(pickyDraft.expandedCategoryIds);
  if (expanded.has(category.id)) expanded.delete(category.id);
  else expanded.add(category.id);
  pickyDraft.expandedCategoryIds = [...expanded];
  persistPickyDraft();

  const section = document.querySelector<HTMLElement>(`[data-picky-category="${category.id}"]`);
  const panel = section?.querySelector<HTMLElement>(".picky-ingredient-panel");
  const button = section?.querySelector<HTMLButtonElement>("[data-action='toggle-picky-category']");
  const isExpanded = expanded.has(category.id);
  if (panel) panel.hidden = !isExpanded;
  if (button) button.setAttribute("aria-expanded", String(isExpanded));
}

function togglePickyCategoryAll(categoryId: string): void {
  const category = pickyCategoryFromId(categoryId);
  if (!category) return;
  const categoryIds = ingredientIdsForCategory(category.id);
  const selectedIds = new Set(pickyDraft.selectedIngredientIds);
  const allSelected = categoryIds.every((id) => selectedIds.has(id));
  categoryIds.forEach((id) => {
    if (allSelected) selectedIds.delete(id);
    else selectedIds.add(id);
  });
  pickyDraft.selectedIngredientIds = [...selectedIds];
  confirmedPickyResult = null;
  pickyExportFeedback = null;
  persistPickyDraft();

  const section = document.querySelector<HTMLElement>(`[data-picky-category="${category.id}"]`);
  section?.querySelectorAll<HTMLInputElement>("[data-picky-ingredient]").forEach((input) => {
    input.checked = !allSelected;
    input.closest(".picky-ingredient")?.classList.toggle("is-selected", !allSelected);
  });
  refreshPickyResult();
  setLive(`${category.name}${allSelected ? "已清空" : "已全部标记为不吃"}`);
}

function resetPickyTest(): void {
  if (pickyDraft.selectedIngredientIds.length === 0 || !window.confirm("清空当前挑食测试？")) return;
  pickyDraft = emptyPickyTestDraft();
  confirmedPickyResult = null;
  pickyExportFeedback = null;
  persistPickyDraft();
  render();
  requestAnimationFrame(() => document.querySelector<HTMLElement>("#picky-page-title")?.focus({ preventScroll: true }));
  setLive("挑食测试已清空");
}

function pickyImageFile(): File | null {
  if (!generatedPickyImage) return null;
  return new File([generatedPickyImage.blob], generatedPickyImage.fileName, { type: "image/png" });
}

function openPickyImagePreview(image: GeneratedPickyImage, result: PickyResult): void {
  disposeGeneratedPickyImage();
  const url = URL.createObjectURL(image.blob);
  generatedPickyImage = { ...image, url };
  const file = pickyImageFile();
  const canShare = Boolean(file && typeof navigator.share === "function" && navigator.canShare?.({ files: [file] }));
  const dialog = document.createElement("dialog");
  dialog.className = "order-image-dialog picky-image-dialog";
  dialog.dataset.pickyImageDialog = "";
  dialog.setAttribute("aria-labelledby", "picky-image-title");
  dialog.innerHTML = `<div class="order-image-dialog-header">
      <div>
        <p class="section-label">PICKY TASTE CLUB</p>
        <h2 id="picky-image-title" tabindex="-1">你的结果海报</h2>
      </div>
      <button class="icon-button" type="button" data-action="close-picky-image" aria-label="关闭预览" title="关闭预览"><i data-lucide="x" aria-hidden="true"></i></button>
    </div>
    <div class="order-image-preview"><img src="${escapeHtml(url)}" alt="${escapeHtml(result.profile.name)}挑食结果预览" width="${image.width}" height="${image.height}" /></div>
    <div class="order-image-actions">
      <button class="primary-button" type="button" data-action="download-picky-image"><i data-lucide="download" aria-hidden="true"></i><span>下载图片</span></button>
      ${canShare ? '<button class="secondary-button" type="button" data-action="share-picky-image"><i data-lucide="share-2" aria-hidden="true"></i><span>分享图片</span></button>' : ""}
    </div>`;
  app.appendChild(dialog);
  dialog.addEventListener("close", () => disposeGeneratedPickyImage(), { once: true });
  initIcons();
  dialog.showModal();
  const preview = dialog.querySelector<HTMLElement>(".order-image-preview");
  if (preview) preview.scrollTop = 0;
  requestAnimationFrame(() => {
    if (preview) preview.scrollTop = 0;
    dialog.querySelector<HTMLElement>("#picky-image-title")?.focus({ preventScroll: true });
  });
}

function downloadPickyImage(): void {
  if (!generatedPickyImage) return;
  const anchor = document.createElement("a");
  anchor.href = generatedPickyImage.url;
  anchor.download = generatedPickyImage.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setLive("挑食结果图片已下载");
}

async function sharePickyImage(): Promise<void> {
  const file = pickyImageFile();
  if (!file || typeof navigator.share !== "function" || !navigator.canShare?.({ files: [file] })) return;
  await navigator.share({ files: [file], title: "我的挑食指数", text: "这是我的饮食人设。" });
}

async function savePickyImage(trigger?: HTMLButtonElement): Promise<void> {
  const result = getPickyResult(pickyDraft.selectedIngredientIds);
  const selectionSignature = [...pickyDraft.selectedIngredientIds].sort().join("|");
  const wasConfirmed = confirmedPickyResult !== null;
  pickyExportFeedback = null;
  const button = trigger ?? document.querySelector<HTMLButtonElement>("[data-action='save-picky-image']");
  if (button) {
    button.disabled = true;
    button.innerHTML = '<i class="spin" data-lucide="loader-circle" aria-hidden="true"></i><span>正在生成</span>';
    initIcons();
  }
  try {
    const image = await generatePickyImage(result);
    if ([...pickyDraft.selectedIngredientIds].sort().join("|") !== selectionSignature) {
      throw new Error("选择已变化，请重新确认生成");
    }
    confirmedPickyResult = result;
    refreshPickyResult();
    openPickyImagePreview(image, result);
    setLive("挑食结果图片已生成");
  } catch (caught) {
    pickyExportFeedback = { kind: "error", message: caught instanceof Error ? caught.message : "图片生成失败，请再试一次" };
    const feedback = document.querySelector<HTMLElement>("[data-picky-export-feedback]");
    if (feedback) {
      feedback.dataset.kind = "error";
      feedback.textContent = pickyExportFeedback.message;
    }
  } finally {
    if (button?.isConnected) {
      button.disabled = false;
      button.innerHTML = wasConfirmed
        ? '<i data-lucide="image-down" aria-hidden="true"></i><span>重新生成海报</span>'
        : '<i data-lucide="check" aria-hidden="true"></i><span>确认并生成</span>';
      initIcons();
    }
  }
}

async function saveGuestOrderImage(): Promise<void> {
  const guest = normalizeGuestName(guestOrder.guestName);
  if (!guest.ok || guestOrder.items.length === 0) {
    updateOrderExportControls();
    document.querySelector<HTMLElement>(guest.ok ? "#order-picker-title" : "#guest-name")?.focus();
    return;
  }
  guestOrder.guestName = guest.display;
  persistGuestOrder();
  orderExportFeedback = null;
  const button = document.querySelector<HTMLButtonElement>("[data-action='save-order-image']");
  if (button) {
    button.disabled = true;
    button.innerHTML = '<i class="spin" data-lucide="loader-circle" aria-hidden="true"></i><span>正在生成</span>';
    initIcons();
  }
  try {
    const image = await generateOrderImage(guest.display, guestOrder.items);
    openOrderImagePreview(image, guest.display);
    setLive("点菜单图片已生成");
  } catch (caught) {
    orderExportFeedback = { kind: "error", message: caught instanceof Error ? caught.message : "图片生成失败，请再试一次" };
    updateOrderExportControls();
  } finally {
    if (button?.isConnected) {
      button.disabled = false;
      button.innerHTML = '<i data-lucide="image-down" aria-hidden="true"></i><span>保存点菜单</span>';
      initIcons();
    }
  }
}

async function submitDish(form: HTMLFormElement): Promise<void> {
  if (state.mode !== "ready") return;
  const input = form.elements.namedItem("dishName");
  const categorySelect = form.elements.namedItem("dishCategory");
  const button = form.querySelector<HTMLButtonElement>("button[type='submit']");
  const feedback = form.querySelector<HTMLElement>("[data-dish-feedback]");
  if (!(input instanceof HTMLInputElement) || !(categorySelect instanceof HTMLSelectElement) || !button || !feedback) return;
  const name = input.value.trim();
  if (!name) {
    feedback.dataset.kind = "error";
    feedback.textContent = "请输入菜名";
    input.focus();
    return;
  }
  if (!categoryValues.has(categorySelect.value)) {
    feedback.dataset.kind = "error";
    feedback.textContent = "请选择菜品分类";
    categorySelect.focus();
    return;
  }
  const category = categorySelect.value as DishCategory;

  button.disabled = true;
  button.innerHTML = '<i class="spin" data-lucide="loader-circle" aria-hidden="true"></i><span>正在加入</span>';
  feedback.textContent = "";
  initIcons();
  try {
    const response = await createDish(state.wheel.slug, name, category);
    state.wheel.dishes.push(response.dish);
    input.value = "";
    categorySelect.value = "";
    feedback.dataset.kind = "success";
    feedback.textContent = `${response.dish.name} 已加入${categoryLabel(response.dish.category)}`;
    document.querySelectorAll<HTMLElement>("[data-pool-count]").forEach((node) => { node.textContent = String(state.mode === "ready" ? state.wheel.dishes.length : 0); });
    filterPool(poolQuery);
    setLive(`${response.dish.name} 已加入共享菜池`);
  } catch (caught) {
    feedback.dataset.kind = "error";
    feedback.textContent = caught instanceof Error ? caught.message : "暂时无法保存，请稍后重试";
    if (caught instanceof ApiError && caught.code === "DISH_ALREADY_EXISTS") input.select();
  } finally {
    button.disabled = false;
    button.innerHTML = '<i data-lucide="plus" aria-hidden="true"></i><span>加入菜池</span>';
    initIcons();
  }
}

app.addEventListener("click", (event) => {
  const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
  if (!target) return;
  event.preventDefault();
  const action = target.dataset.action;
  if (action === "spin") spin();
  else if (action === "select-category") selectCategory(target.dataset.category ?? "");
  else if (action === "select-order-category") selectOrderCategory(target.dataset.category ?? "");
  else if (action === "open-pool") setAppView("pool");
  else if (action === "open-order") setAppView("order");
  else if (action === "open-picky") setAppView("picky");
  else if (action === "return-wheel") setAppView("wheel");
  else if (action === "toggle-order-dish") toggleGuestOrderDish(Number(target.dataset.orderDishId));
  else if (action === "remove-order-item") removeGuestOrderItem(target.dataset.orderItemKey ?? "");
  else if (action === "clear-order") clearGuestOrder();
  else if (action === "view-order-summary") document.querySelector("#order-summary")?.scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth", block: "start" });
  else if (action === "focus-custom-order") document.querySelector<HTMLInputElement>("#custom-order-name")?.focus();
  else if (action === "save-order-image") void saveGuestOrderImage();
  else if (action === "download-order-image") downloadOrderImage();
  else if (action === "share-order-image") void shareOrderImage().catch((caught: unknown) => {
    if (!(caught instanceof DOMException && caught.name === "AbortError")) setLive("暂时无法分享图片");
  });
  else if (action === "close-order-image") document.querySelector<HTMLDialogElement>("[data-order-image-dialog]")?.close();
  else if (action === "toggle-picky-category") togglePickyCategory(target.dataset.pickyCategoryId ?? "");
  else if (action === "toggle-picky-category-all") togglePickyCategoryAll(target.dataset.pickyCategoryId ?? "");
  else if (action === "view-picky-result") document.querySelector("#picky-result")?.scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth", block: "start" });
  else if (action === "reset-picky-test") resetPickyTest();
  else if (action === "save-picky-image") void savePickyImage(target instanceof HTMLButtonElement ? target : undefined);
  else if (action === "download-picky-image") downloadPickyImage();
  else if (action === "share-picky-image") void sharePickyImage().catch((caught: unknown) => {
    if (!(caught instanceof DOMException && caught.name === "AbortError")) setLive("暂时无法分享图片");
  });
  else if (action === "close-picky-image") document.querySelector<HTMLDialogElement>("[data-picky-image-dialog]")?.close();
  else if (action === "clear-pool-search") {
    poolQuery = "";
    const input = document.querySelector<HTMLInputElement>("[data-pool-search]");
    if (input) input.value = "";
    filterPool("");
    target.hidden = true;
    input?.focus();
  }
  else if (action === "clear-order-search") {
    orderQuery = "";
    const input = document.querySelector<HTMLInputElement>("[data-order-search]");
    if (input) input.value = "";
    refreshOrderCatalog();
    target.hidden = true;
    input?.focus();
  }
  else if (action === "share") void shareWheel().catch(() => setLive("分享已取消"));
  else if (action === "retry") void load();
});

app.addEventListener("submit", (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.matches("[data-dish-form], [data-custom-order-form]")) return;
  event.preventDefault();
  if (form.matches("[data-custom-order-form]")) submitCustomOrder(form);
  else void submitDish(form);
});

app.addEventListener("input", (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement && input.matches("[data-pool-search]")) {
    poolQuery = input.value;
    filterPool(input.value);
    const clear = document.querySelector<HTMLElement>("[data-action='clear-pool-search']");
    if (clear) clear.hidden = input.value.length === 0;
  }
  else if (input instanceof HTMLInputElement && input.matches("[data-order-search]")) {
    orderQuery = input.value;
    refreshOrderCatalog();
    const clear = document.querySelector<HTMLElement>("[data-action='clear-order-search']");
    if (clear) clear.hidden = input.value.length === 0;
  }
  else if (input instanceof HTMLInputElement && input.matches("[data-order-name]")) {
    guestOrder.guestName = input.value;
    orderExportFeedback = null;
    persistGuestOrder();
    updateOrderExportControls();
  }
});

app.addEventListener("change", (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches("[data-picky-ingredient]")) return;
  togglePickyIngredient(input.value, input.checked);
});

app.addEventListener("error", (event) => {
  const image = event.target;
  if (!(image instanceof HTMLImageElement) || !image.matches("[data-dish-image]")) return;
  const media = image.closest<HTMLElement>(".dish-media");
  if (!media) return;
  media.innerHTML = dishPlaceholderMarkup();
  initIcons();
}, true);

window.addEventListener("popstate", () => {
  appView = viewFromLocation();
  poolQuery = "";
  orderQuery = "";
  orderCategory = "all";
  if (state.mode === "ready") render();
});

async function load(): Promise<void> {
  state = { mode: "loading" };
  winner = null;
  spinning = false;
  selectedCategory = "all";
  rotation = 0;
  render();
  try {
    const wheel = await getWheel(currentSlug());
    candidates = sampleWithoutReplacement(wheel.dishes, CANDIDATE_COUNT);
    state = { mode: "ready", wheel };
    try {
      guestOrder = parseGuestOrderDraft(localStorage.getItem(orderStorageKey(wheel.slug)), wheel.dishes);
    } catch {
      guestOrder = emptyGuestOrderDraft();
    }
    try {
      pickyDraft = parsePickyTestDraft(localStorage.getItem("food-wheel:picky-test:v1"));
    } catch {
      pickyDraft = emptyPickyTestDraft();
    }
  } catch (caught) {
    state = { mode: "error", message: caught instanceof Error ? caught.message : "暂时无法打开共享菜池" };
  }
  render();
}

void load();
